import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useUploadWizard } from '../../../contexts/UploadWizardContext';
import { CreativeAssignmentMode, UploadStep } from '../../../types/upload';

const WizardNavigation2: React.FC = () => {
  const {
    currentStep,
    canGoNext,
    canGoPrevious,
    goToNextStep,
    goToPreviousStep,
    validateCurrentStep,
    setStepValidation,
    state
  } = useUploadWizard();

  const validation = validateCurrentStep();

  // Update step validation when validation changes
  useEffect(() => {
    setStepValidation(currentStep, validation.isValid);
  }, [validation.isValid, currentStep, setStepValidation]);

  const getNextButtonText = (): string => {
    switch (currentStep) {
      case UploadStep.FILE_SELECTION:
        return 'Continue to Production';
      case UploadStep.PRODUCTION_SELECTION:
        return state.assignmentMode === CreativeAssignmentMode.EXISTING ? 'Review & Upload' : 'Continue to Metadata';
      case UploadStep.INDIVIDUAL_METADATA:
        return 'Continue to Tagging';
      case UploadStep.TAG:
        return 'Review & Upload';
      case UploadStep.REVIEW_UPLOAD:
        return 'Ready to Upload';
      default:
        return 'Next';
    }
  };

  const getPreviousButtonText = (): string => {
    switch (currentStep) {
      case UploadStep.PRODUCTION_SELECTION:
        return 'Back to Files';
      case UploadStep.INDIVIDUAL_METADATA:
        return 'Back to Production';
      case UploadStep.TAG:
        return 'Back to Metadata';
      case UploadStep.REVIEW_UPLOAD:
        return state.assignmentMode === CreativeAssignmentMode.EXISTING ? 'Back to Production' : 'Back to Tagging';
      default:
        return 'Previous';
    }
  };

  const isUploadStep = currentStep === UploadStep.REVIEW_UPLOAD;

  return (
    <Box>
      {/* Validation Errors/Warnings */}
      {(validation.errors.length > 0 || validation.warnings.length > 0) && (
        <Box sx={{ mb: 2 }}>
          {validation.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Box>
                {validation.errors.map((error, index) => (
                  <div key={index}>• {error}</div>
                ))}
              </Box>
            </Alert>
          )}
          {validation.warnings.length > 0 && (
            <Alert severity="warning">
              <Box>
                {validation.warnings.map((warning, index) => (
                  <div key={index}>• {warning}</div>
                ))}
              </Box>
            </Alert>
          )}
        </Box>
      )}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={goToPreviousStep}
          disabled={!canGoPrevious || state.isUploading}
          sx={{ minWidth: '150px' }}
        >
          {getPreviousButtonText()}
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isUploadStep && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goToNextStep}
              disabled={!canGoNext || state.isUploading}
              sx={{ minWidth: '200px' }}
            >
              {getNextButtonText()}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WizardNavigation2;